
import { getOsrmRoute } from './routing/osrm';
import { DistanceService } from './logistics/core/distanceService';
import { getGoogleMapsDistanceViaEdge } from './googleMapsEdge';
import { RoutingOsrmCalibrationConfig } from '../types';
import { calibrateOsrmMinutes } from './logistics/core/osrmCalibration';

/**
 * v2025.02.08: Routing API Comparison Service
 * Ferramenta para comparar precisão entre OSRM (gratuito) e Google Maps (pago)
 * usando endereços reais de Maceió-AL.
 */

// Endereço do Hub (Loja) em Maceió
export const MACEIO_HUB = {
    address: 'Av. Pretestato Ferreira Machado, 1526, Jatiúca, Maceió-AL',
    lat: -9.6658,
    lng: -35.7353
};

// Endereços de teste em Maceió (diferentes regiões e distâncias)
// Observação: lat/lng abaixo são fallback. O teste tenta geocodificar pelo Google primeiro.
export const MACEIO_TEST_DESTINATIONS = [
    { name: 'Pajuçara (Próximo)', address: 'Av. Dr. Antônio Gouveia, 1520, Pajuçara, Maceió-AL', lat: -9.6742, lng: -35.7281 },
    { name: 'Ponta Verde (Médio)', address: 'Av. Álvaro Otacílio, 2000, Ponta Verde, Maceió-AL', lat: -9.6531, lng: -35.7019 },
    { name: 'Cruz das Almas (Longo)', address: 'Rua do Livramento, 450, Cruz das Almas, Maceió-AL', lat: -9.6389, lng: -35.7417 },
    { name: 'Benedito Bentes (Longo)', address: 'Av. Durval de Góes, 1800, Benedito Bentes, Maceió-AL', lat: -9.6234, lng: -35.7556 },
    { name: 'Tabuleiro (Muito Longo)', address: 'Av. Menino Marcelo, 3800, Tabuleiro, Maceió-AL', lat: -9.5923, lng: -35.7890 },
    { name: 'Gruta de Lourdes (Norte)', address: 'Rua da Gruta, 123, Gruta de Lourdes, Maceió-AL', lat: -9.6891, lng: -35.7502 },
    { name: 'Feitosa (Oeste)', address: 'Av. Fernandes Lima, 3500, Feitosa, Maceió-AL', lat: -9.6123, lng: -35.7234 },
    { name: 'Serraria (Sul)', address: 'Av. Comendador Leão, 2800, Serraria, Maceió-AL', lat: -9.7012, lng: -35.7123 }
];

export interface ComparisonResult {
    destination: typeof MACEIO_TEST_DESTINATIONS[0];
    osrm: {
        distanceKm: number;
        durationMinsRaw: number;
        durationMinsCalibrated: number;
    } | null;
    google: { distanceKm: number; durationMins: number; source?: string; routeSummary?: string | null; } | null;
    heuristic: { distanceKm: number; durationMins: number; };
    testedPoints: {
        origin: { lat: number; lng: number; };
        destination: { lat: number; lng: number; };
    } | null;
    differences: {
        distancePercent: number;
        durationRawPercent: number;
        durationCalibratedPercent: number;
    } | null;
    googleError?: string; // v2025.02.08: Add error info for debugging
}

const resolveCoordinatesForComparison = async (
    address: string,
    googleMapsApiKey: string | undefined,
    fallback: { lat: number; lng: number; }
): Promise<{ lat: number; lng: number; } | null> => {
    if (!googleMapsApiKey) return fallback;

    try {
        // Bypass de cache para o teste de comparacao refletir o geocode atual do Google
        const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${googleMapsApiKey}`;
        const response = await fetch(geocodeUrl);
        const data = await response.json();

        if (data.status === 'OK' && data.results?.[0]?.geometry?.location) {
            const location = data.results[0].geometry.location;
            return { lat: location.lat, lng: location.lng };
        }
    } catch (error) {
        console.warn('[RoutingComparison] Geocode ao vivo falhou, usando fallback:', error);
    }

    return fallback;
};

/**
 * Compara resultados entre OSRM e Google para um destino específico
 */
export const compareRoutingApis = async (
    destination: typeof MACEIO_TEST_DESTINATIONS[0],
    googleMapsApiKey?: string,
    hubCoordsOverride?: { lat: number; lng: number; },
    osrmCalibration?: RoutingOsrmCalibrationConfig
): Promise<ComparisonResult> => {
    const hubCoords = hubCoordsOverride || await resolveCoordinatesForComparison(
        MACEIO_HUB.address,
        googleMapsApiKey,
        { lat: MACEIO_HUB.lat, lng: MACEIO_HUB.lng }
    );

    const destinationCoords = await resolveCoordinatesForComparison(
        destination.address,
        googleMapsApiKey,
        { lat: destination.lat, lng: destination.lng }
    );

    if (!hubCoords || !destinationCoords) {
        return {
            destination,
            osrm: null,
            google: null,
            heuristic: { distanceKm: 0, durationMins: 0 },
            testedPoints: null,
            differences: null,
            googleError: 'Nao foi possivel resolver coordenadas para o teste'
        };
    }

    // OSRM (Free)
    const osrmRoute = await getOsrmRoute(hubCoords.lat, hubCoords.lng, destinationCoords.lat, destinationCoords.lng);
    const osrmResult = osrmRoute
        ? {
            distanceKm: osrmRoute.distanceKm,
            durationMinsRaw: osrmRoute.durationMins,
            durationMinsCalibrated: calibrateOsrmMinutes({
                rawDurationMins: osrmRoute.durationMins,
                distanceKm: osrmRoute.distanceKm,
                calibration: osrmCalibration
            })
        }
        : null;
    
    // Google (Paid) - Usando Edge Function para evitar CORS
    let googleResult: { distanceKm: number; durationMins: number; source?: string; routeSummary?: string | null; } | null = null;
    let googleError: string | undefined;
    
    if (googleMapsApiKey) {
        try {
            const googleData = await getGoogleMapsDistanceViaEdge(
                hubCoords.lat, hubCoords.lng,
                destinationCoords.lat, destinationCoords.lng,
                googleMapsApiKey
            );

            if (googleData && googleData.distanceKm > 0) {
                googleResult = {
                    distanceKm: googleData.distanceKm,
                    durationMins: googleData.durationMins,
                    source: googleData.source,
                    routeSummary: googleData.routeSummary
                };
            } else {
                console.warn(`[RoutingComparison] Edge Function retornou dados vazios para ${destination.name}`);
                googleError = 'Dados vazios retornados';
            }
        } catch (e: any) {
            console.error(`[RoutingComparison] Erro na Edge Function para ${destination.name}:`, e.message);
            googleError = e.message;
        }
    } else {
        console.warn(`[RoutingComparison] Google Maps API Key não informada`);
        googleError = 'API Key não informada';
    }
    
    // Heuristic (Haversine + factor)
    const heuristicDist = DistanceService.getDistanceSync(hubCoords.lat, hubCoords.lng, destinationCoords.lat, destinationCoords.lng);
    const heuristicResult = {
        distanceKm: heuristicDist,
        durationMins: Math.ceil(heuristicDist * 3.0) // 3 min/km fallback
    };
    
    // Calculate differences
    let differences = null;
    if (osrmResult && googleResult) {
        differences = {
            distancePercent: ((osrmResult.distanceKm - googleResult.distanceKm) / googleResult.distanceKm) * 100,
            durationRawPercent: ((osrmResult.durationMinsRaw - googleResult.durationMins) / googleResult.durationMins) * 100,
            durationCalibratedPercent: ((osrmResult.durationMinsCalibrated - googleResult.durationMins) / googleResult.durationMins) * 100
        };
    }
    
    return {
        destination,
        osrm: osrmResult,
        google: googleResult,
        heuristic: heuristicResult,
        testedPoints: {
            origin: hubCoords,
            destination: destinationCoords
        },
        differences,
        googleError
    };
};

/**
 * Executa comparação completa para todos os destinos de teste
 */
export const runFullComparison = async (
    googleMapsApiKey?: string,
    osrmCalibration?: RoutingOsrmCalibrationConfig
): Promise<ComparisonResult[]> => {
    const results: ComparisonResult[] = [];
    const resolvedHubCoords = await resolveCoordinatesForComparison(
        MACEIO_HUB.address,
        googleMapsApiKey,
        { lat: MACEIO_HUB.lat, lng: MACEIO_HUB.lng }
    );
    
    for (const destination of MACEIO_TEST_DESTINATIONS) {
        try {
            const result = await compareRoutingApis(destination, googleMapsApiKey, resolvedHubCoords || undefined, osrmCalibration);
            results.push(result);
        } catch (e) {
            console.error(`[RoutingComparison] Erro ao comparar ${destination.name}:`, e);
        }
    }
    
    return results;
};

/**
 * Calcula estatísticas de precisão da comparação
 */
export const calculateComparisonStats = (results: ComparisonResult[]) => {
    const validResults = results.filter(r => r.differences !== null);
    
    if (validResults.length === 0) {
        return {
            avgDistanceDeviation: 0,
            avgDurationRawDeviation: 0,
            avgDurationCalibratedDeviation: 0,
            maxDistanceDeviation: 0,
            maxDurationRawDeviation: 0,
            maxDurationCalibratedDeviation: 0,
            recommendation: 'Sem dados suficientes para análise'
        };
    }
    
    const avgDistanceDeviation = validResults.reduce((sum, r) => sum + Math.abs(r.differences!.distancePercent), 0) / validResults.length;
    const avgDurationRawDeviation = validResults.reduce((sum, r) => sum + Math.abs(r.differences!.durationRawPercent), 0) / validResults.length;
    const avgDurationCalibratedDeviation = validResults.reduce((sum, r) => sum + Math.abs(r.differences!.durationCalibratedPercent), 0) / validResults.length;
    const maxDistanceDeviation = Math.max(...validResults.map(r => Math.abs(r.differences!.distancePercent)));
    const maxDurationRawDeviation = Math.max(...validResults.map(r => Math.abs(r.differences!.durationRawPercent)));
    const maxDurationCalibratedDeviation = Math.max(...validResults.map(r => Math.abs(r.differences!.durationCalibratedPercent)));
    
    let recommendation = '';
    if (avgDistanceDeviation < 10 && avgDurationCalibratedDeviation < 15) {
        recommendation = 'Calibracao OSRM está muito precisa. API gratuita recomendada.';
    } else if (avgDistanceDeviation < 20 && avgDurationCalibratedDeviation < 25) {
        recommendation = 'Calibracao OSRM está aceitável. API gratuita recomendada para economia.';
    } else {
        recommendation = 'Mesmo calibrado, há desvio significativo. Considere Google em operações críticas.';
    }
    
    return {
        avgDistanceDeviation: parseFloat(avgDistanceDeviation.toFixed(2)),
        avgDurationRawDeviation: parseFloat(avgDurationRawDeviation.toFixed(2)),
        avgDurationCalibratedDeviation: parseFloat(avgDurationCalibratedDeviation.toFixed(2)),
        maxDistanceDeviation: parseFloat(maxDistanceDeviation.toFixed(2)),
        maxDurationRawDeviation: parseFloat(maxDurationRawDeviation.toFixed(2)),
        maxDurationCalibratedDeviation: parseFloat(maxDurationCalibratedDeviation.toFixed(2)),
        recommendation
    };
};

/**
 * Gera relatório de comparação em formato legível
 */
export const generateComparisonReport = (results: ComparisonResult[]): string => {
    const stats = calculateComparisonStats(results);
    
    let report = `📊 RELATÓRIO DE COMPARAÇÃO DE APIs DE ROTEAMENTO\n`;
    report += `📍 Hub: ${MACEIO_HUB.address}\n`;
    report += `🔬 Metodologia: mesmas coordenadas para OSRM e Google (geocodificadas a partir dos endereços)\n`;
    report += `🛣️ Critério: menor tempo em cada provedor (Google usa rota mais rápida entre alternativas)\n`;
    report += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    
    // Check if Google API was called
    const googleCalled = results.some(r => r.google !== null);
    const googleErrors = results.filter(r => r.googleError).map(r => ({ name: r.destination.name, error: r.googleError }));
    
    if (!googleCalled && googleErrors.length > 0) {
        report += `⚠️  PROBLEMA DETECTADO:\n`;
        report += `   O Google Maps não foi chamado em nenhum destino.\n`;
        report += `   Verifique a API Key e tente novamente.\n\n`;
        
        // Show first error
        if (googleErrors.length > 0) {
            report += `📝 Erro em "${googleErrors[0].name}": ${googleErrors[0].error}\n\n`;
        }
    }
    
        report += `📈 ESTATÍSTICAS GERAIS:\n`;
    if (googleCalled) {
        report += `   Desvio médio de distância: ${stats.avgDistanceDeviation}%\n`;
        report += `   Desvio médio tempo OSRM bruto: ${stats.avgDurationRawDeviation}%\n`;
        report += `   Desvio médio tempo OSRM calibrado: ${stats.avgDurationCalibratedDeviation}%\n`;
        report += `   Máximo desvio distância: ${stats.maxDistanceDeviation}%\n`;
        report += `   Máximo desvio tempo OSRM bruto: ${stats.maxDurationRawDeviation}%\n`;
        report += `   Máximo desvio tempo OSRM calibrado: ${stats.maxDurationCalibratedDeviation}%\n\n`;
        
        report += `💡 RECOMENDAÇÃO:\n   ${stats.recommendation}\n\n`;
    } else {
        report += `   (Sem dados suficientes - Google não respondeu)\n\n`;
        report += `💡 RECOMENDAÇÃO:\n   Configure uma API Key válida para realizar a comparação.\n\n`;
    }
    
    report += `📍 DETALHES POR DESTINO:\n`;
    results.forEach((r, idx) => {
        report += `\n${idx + 1}. ${r.destination.name}\n`;
        report += `   📍 ${r.destination.address}\n`;
        if (r.testedPoints) {
            report += `   🧭 Coordenadas testadas: ${r.testedPoints.destination.lat.toFixed(5)}, ${r.testedPoints.destination.lng.toFixed(5)}\n`;
        }
        if (r.osrm) {
            report += `   🆓 OSRM bruto: ${r.osrm.distanceKm.toFixed(2)}km / ${r.osrm.durationMinsRaw.toFixed(0)}min\n`;
            report += `   ⚙️ OSRM calibrado: ${r.osrm.distanceKm.toFixed(2)}km / ${r.osrm.durationMinsCalibrated.toFixed(0)}min\n`;
        }
        if (r.google) {
            report += `   💰 Google: ${r.google.distanceKm.toFixed(2)}km / ${r.google.durationMins.toFixed(0)}min\n`;
            if (r.google.source) report += `   🔧 Fonte Google: ${r.google.source}\n`;
            if (r.google.routeSummary) report += `   🛣️ Rota Google: ${r.google.routeSummary}\n`;
        }
        if (r.googleError && !r.google) {
            report += `   ⚠️  Google: ERRO - ${r.googleError}\n`;
        }
        if (r.differences) {
            report += `   📊 Distância: ${r.differences.distancePercent > 0 ? '+' : ''}${r.differences.distancePercent.toFixed(1)}%\n`;
            report += `   📊 Tempo bruto vs Google: ${r.differences.durationRawPercent > 0 ? '+' : ''}${r.differences.durationRawPercent.toFixed(1)}%\n`;
            report += `   📊 Tempo calibrado vs Google: ${r.differences.durationCalibratedPercent > 0 ? '+' : ''}${r.differences.durationCalibratedPercent.toFixed(1)}%\n`;
        }
    });
    
    return report;
};
